package fyi.moko.hotpl.domain.model

import kotlinx.serialization.Serializable

/**
 * One photo attached to a [Place] or a [Visit]: the file name in the local image dir, plus what
 * the photo turned out to be.
 *
 * ── 파일명은 동기화되고 바이트는 기기에 남는다 ─────────────────────────
 * [Place.imageFileName]과 같은 규칙이다. 다른 기기에서 받으면 이름은 있고 파일은 없으므로
 * 로드가 null을 돌려주고 UI는 사진 없는 자리로 그린다 — 깨진 이미지가 아니라 없는 것.
 *
 * [kind]가 null이면 아직 분류하지 않았거나 분류에 실패한 것이다. 목록 순서가 곧 표시 순서이며,
 * 대표 사진은 순서가 아니라 [PhotoKind.COVER_PRIORITY]로 고른다.
 */
@Serializable
data class PhotoRef(
    val fileName: String,
    val kind: PhotoKind? = null,
    /**
     * 종류까지는 못 가렸지만 **어느 묶음인지는 아는** 경우를 담는다. [kind]가 있으면 이 값은
     * 무시된다([resolvedGroup] 참고).
     *
     * 이런 상태가 필요한 이유는 분류기가 실제로 그렇게 동작하기 때문이다. 실측에서 ML Kit
     * 라벨로 `외관`과 `내부`를 가르는 건 14장 중 9장에 그쳤다 — 유리 너머 내부가 보이는 근접
     * 외관 사진과 하늘이 없는 야간 사진이 무너진다. 반면 **가게냐 방문이냐**는 37장 중 36장이
     * 맞았다. 못 가린 걸 아무 종류로나 채우면 화면에 정해지지 않은 값이 확정된 것처럼 뜬다.
     */
    val group: PhotoGroup? = null,
)

/**
 * 이 사진이 어느 묶음인지. 종류를 알면 거기서 나오고, 모르면 [PhotoRef.group]을 쓴다.
 * 둘 다 없으면 아직 아무것도 모르는 사진이다(분류 전이거나 v15로 올라온 옛 사진).
 */
val PhotoRef.resolvedGroup: PhotoGroup?
    get() = kind?.group ?: group

/**
 * 목록에서 대표로 쓸 사진의 파일명. [Place.imageFileName]/[Visit.imageFileName]에 넣을 값이며,
 * **이 함수 하나로만 정한다** — 저장·동기화·백업이 각자 계산하면 반드시 어긋난다.
 *
 * [PhotoKind.COVER_PRIORITY]가 먼저다. 거기서 아무것도 못 고르면(사람·기타뿐이거나 아직
 * 분류되지 않은 사진들) **첫 사진으로 떨어진다.** 화면은 자동 대표를 비워 두더라도 이 필드는
 * 채워야 하기 때문이다 — 아직 [PhotoRef]를 모르는 구버전 클라이언트에게는 이 값이 사진의
 * 전부여서, 비우면 그쪽에서 사진이 사라진다. v15 마이그레이션으로 올라온 사진들이 정확히
 * 이 경우다(분류 없이 목록에 들어간다).
 */
fun List<PhotoRef>.coverFileName(): String? =
    PhotoKind.pickCover(this) { it.kind }?.fileName ?: firstOrNull()?.fileName

/**
 * [Place.imageFileName]을 [Place.photos]에 맞춰 놓은 사본. **저장 직전에 부른다.**
 *
 * ── 왜 저장 경로마다 불러야 하는가 ─────────────────────────────
 * 이 규칙이 Room 매퍼 안에만 있던 동안 웹(Room을 안 쓴다)은 통째로 건너뛰었다. 그 결과 웹에서
 * 기록한 방문은 사진이 있어도 [Visit.imageFileName]이 계속 null이었고, 가게 갤러리에서 대표
 * 사진을 지우면 이미 삭제된 파일명이 대표 자리에 남았다. 둘 다 화면에는 안 보이고
 * (화면은 [Place.photos]를 읽는다) **동기화와 구버전 클라이언트만** 물리는 종류라 실기기에서도
 * 안 잡혔다 — 웹을 띄워 저장된 값을 직접 열어보고 나서야 나왔다.
 *
 * `?: imageFileName` 폴백은 남겨 둔다. photos가 비었는데 imageFileName은 있는 행이 실제로
 * 생길 수 있기 때문이다 — [fyi.moko.hotpl.data.local.Converters.toPhotos]가 JSON 파싱에
 * 실패하면 빈 목록을 돌려준다(사진 없는 장소로 보이는 편이 낫다는 판단). 그때 폴백까지 없으면
 * 다음 저장에서 남은 파일명 하나마저 지워져 사진으로 돌아갈 길이 끊긴다.
 *
 * 사진을 **전부 지운 것**을 반영하려면 호출 전에 `imageFileName = null`로 두면 된다
 * (폴백이 null을 만나 null로 떨어진다). 장소 편집 폼이 그렇게 한다.
 */
fun Place.withDerivedCover(): Place =
    copy(imageFileName = imageFileName.keptIfStillPresentIn(photos) ?: photos.coverFileName() ?: imageFileName)

/** [Place.withDerivedCover]와 같은 규칙의 [Visit] 판. */
fun Visit.withDerivedCover(): Visit =
    copy(imageFileName = imageFileName.keptIfStillPresentIn(photos) ?: photos.coverFileName() ?: imageFileName)

/**
 * 이미 정해진 대표가 아직 [photos] 안에 살아 있으면 그 값, 아니면 null.
 *
 * ── 왜 "덮어쓰기"에서 "이미 정해졌으면 존중"으로 바뀌었나 ──────────────────────
 * 예전에는 저장할 때마다 [coverFileName]으로 다시 뽑아 무조건 덮었다. 그래서 사용자가 갤러리에서
 * 고른 대표 사진이 다음 저장에 바로 사라졌다 — 간판 > 음식 > 내부 > 메뉴판 우선순위가 늘 이겼기
 * 때문이다. 목록에서 이 가게를 알아보게 하는 사진이 어느 것인지는 결국 본인이 안다.
 *
 * 불변식은 그대로다: 이 필드는 **언제나 [photos] 안의 파일**을 가리키고, 사진이 있는데 비어 있는
 * 일은 없다. 지워진 사진이 대표에 남는 문제도 그대로 막힌다 — 목록에 없으면 여기서 null이 되어
 * 자동 선택으로 떨어진다.
 *
 * 잃는 것: 자동 승격. 메뉴판만 있던 장소에 나중에 간판 사진을 넣어도 대표가 알아서 간판으로
 * 올라가지는 않는다. 사용자가 모르는 사이에 대표가 바뀌던 동작이라, 안정적인 쪽이 낫다고 봤다.
 */
private fun String?.keptIfStillPresentIn(photos: List<PhotoRef>): String? =
    this?.takeIf { name -> photos.any { it.fileName == name } }
